import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../api';

// One coach's invoice for one month, laid out to be printed.
//
// WHOSE DOCUMENT THIS IS. The coach is the supplier and the club is the
// customer, which is the other way round to every other screen here. Everything
// below follows from that: the coach's name and address sit at the top where a
// letterhead would be, and the club sits under "To".
//
// NO VAT LINE, EVER. See the comment on the route in backend/src/routes/
// bills.js. It is not an omission and it must not be "fixed".
//
// WHY THERE IS NO PDF LIBRARY. The browser's own print dialogue produces a PDF
// that is selectable, searchable and the right size, on every machine the club
// owns, for no dependency and no server work. The print stylesheet at the
// bottom of index.css is what makes it come out as a document rather than a
// screenshot of a web page. When invoices start being EMAILED to coaches this
// will need a real PDF generated server-side, and that is the point to add one
// rather than now.

const money = (n) => `£${Number(n || 0).toFixed(2)}`;
const ukDate = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// '2026-10' -> 'October 2026'. The period is a label, not a date, so it is
// built by hand rather than put through a Date, which would read it as the
// first of the month in whatever timezone the laptop is in.
const periodName = (label) => {
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const m = /^(\d{4})-(\d{2})$/.exec(label || '');
  if (!m) return label || '';
  const idx = Number(m[2]) - 1;
  return months[idx] ? `${months[idx]} ${m[1]}` : label;
};

// An address is typed into a textarea, so it arrives with real newlines in it.
const addressLines = (text) => String(text || '')
  .split(/\r?\n/)
  .map(l => l.trim())
  .filter(Boolean);

const Invoice = () => {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    api.get(`/api/bills/${id}/invoice`)
      .then(res => { if (!cancelled) setInv(res.data); })
      .catch(err => {
        if (cancelled) return;
        setError(err.response?.data?.error || 'That invoice could not be loaded.');
      });
    return () => { cancelled = true; };
  }, [id]);

  if (error) {
    return (
      <div className="container">
        <div className="card">
          <div className="error-msg prewrap">{error}</div>
          <Link to="/bills">Back to bills</Link>
        </div>
      </div>
    );
  }
  if (!inv) return <div className="container"><p className="muted">Loading…</p></div>;

  const coachAddress = addressLines(inv.coach.address);
  const clubAddress = addressLines(inv.club.registered_address);

  return (
    <div className="container">
      {/* Everything in here is hidden when printing. What lands on paper is
          the invoice sheet below and nothing else. */}
      <div className="invoice-toolbar no-print">
        <Link to="/bills">← Back to bills</Link>
        <button className="btn" onClick={() => window.print()}>Print or save as PDF</button>
      </div>

      {!inv.coach.address && (
        <div className="notice-msg no-print">
          This coach has not given a billing address yet, so the invoice has no address
          under their name. They can add one on their own profile page.
        </div>
      )}
      {!inv.bank && (
        <div className="notice-msg no-print">
          No bank details are on file for this coach, so there is nothing on the invoice
          saying where to pay. They can add them on their own profile page.
        </div>
      )}

      <div className="invoice-sheet">
        <div className="invoice-head">
          <div>
            <h1 className="invoice-title">Invoice</h1>
            <div className="invoice-meta">
              <div><span>Invoice number</span><strong>{inv.invoice_number || 'Not numbered'}</strong></div>
              <div><span>Date</span><strong>{ukDate(inv.issued_at)}</strong></div>
              <div><span>Period</span><strong>{periodName(inv.period_label)}</strong></div>
            </div>
          </div>
          {/* The status is a fact about the club's books, not part of the
              document, so it is kept off the printed sheet. */}
          <div className="no-print">
            <span className={`badge ${inv.status}`}>{inv.status === 'paid' ? 'Paid' : 'Awaiting payment'}</span>
            {inv.paid_at && <div className="muted" style={{ marginTop: '0.3rem' }}>Paid {ukDate(inv.paid_at)}</div>}
          </div>
        </div>

        <div className="invoice-parties">
          <div>
            <h2>From</h2>
            <p className="invoice-party">
              <strong>{inv.coach.name}</strong>
              {coachAddress.map((l, i) => <React.Fragment key={i}><br />{l}</React.Fragment>)}
              {inv.coach.email && <><br />{inv.coach.email}</>}
            </p>
          </div>
          <div>
            <h2>To</h2>
            <p className="invoice-party">
              <strong>{inv.club.legal_name}</strong>
              {inv.club.brand_name && inv.club.brand_name !== inv.club.legal_name
                && <><br />trading as {inv.club.brand_name}</>}
              {clubAddress.map((l, i) => <React.Fragment key={i}><br />{l}</React.Fragment>)}
              {inv.club.company_number && <><br />Company number {inv.club.company_number}</>}
            </p>
          </div>
        </div>

        <table className="invoice-lines">
          <thead>
            <tr>
              <th>Date</th>
              <th>Session</th>
              <th className="num">Hours</th>
              <th className="num">Rate</th>
              <th className="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {inv.lines.map((l, i) => (
              <React.Fragment key={i}>
                <tr>
                  <td>{ukDate(l.date)}</td>
                  <td>
                    {l.session_type === 'away_game' && l.venue_name
                      ? `Away game at ${l.venue_name}`
                      : (l.description || 'Coaching session')}
                  </td>
                  <td className="num">{Number(l.hours).toFixed(2)}</td>
                  <td className="num">{money(l.rate_used)}</td>
                  <td className="num">{money(l.hours_amount)}</td>
                </tr>
                {/* Mileage is its own line under the session it belongs to,
                    rather than one lump at the bottom, so a coach can check a
                    journey they actually remember making. */}
                {Number(l.miles) > 0 && (
                  <tr className="invoice-subline">
                    <td></td>
                    <td>
                      Mileage{l.venue_name ? `, ${l.venue_name} and back` : ''}
                      {' '}({Number(l.miles).toFixed(1)} miles at {money(l.mileage_rate_used)} a mile)
                    </td>
                    <td className="num"></td>
                    <td className="num"></td>
                    <td className="num">{money(l.mileage_amount)}</td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {inv.lines.length === 0 && (
              <tr><td colSpan={5} className="muted">No sessions on this invoice.</td></tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={4} className="num">Coaching, {Number(inv.totals.hours).toFixed(2)} hours</td>
              <td className="num">{money(inv.totals.hours_amount)}</td>
            </tr>
            {Number(inv.totals.miles) > 0 && (
              <tr>
                <td colSpan={4} className="num">Mileage, {Number(inv.totals.miles).toFixed(1)} miles</td>
                <td className="num">{money(inv.totals.mileage_amount)}</td>
              </tr>
            )}
            <tr className="invoice-total">
              <td colSpan={4} className="num">Total due</td>
              <td className="num">{money(inv.totals.total)}</td>
            </tr>
          </tfoot>
        </table>

        <div className="invoice-footer">
          {inv.bank && (
            <div className="invoice-pay">
              <h2>Please pay by bank transfer to</h2>
              <p className="invoice-party">
                {inv.bank.account_name}<br />
                Sort code {inv.bank.sort_code}<br />
                Account number {inv.bank.account_number}<br />
                Reference {inv.invoice_number || inv.period_label}
              </p>
            </div>
          )}
          {/* Says what it is rather than leaving a reader to wonder why there
              is no VAT line. */}
          <p className="invoice-note">No VAT is chargeable on this invoice.</p>
        </div>
      </div>
    </div>
  );
};

export default Invoice;
